export type Portal = "fork" | "hire" | "seek" | "admin";

export type PortalMetadata = {
  title: string;
  description: string;
  favicon: string;
  appleTouchIcon: string;
  socialPreview: string;
  themeColor: string;
  imageAlt: string;
};

export const portalMetadata: Record<Portal, PortalMetadata> = {
  fork: {
    title: "Fork - AI Hiring and Career Platform",
    description: "Fork connects hiring teams and candidates across ForkHire, ForkSeek, and ForkAdmin.",
    favicon: "/brand/fork/mark.svg",
    appleTouchIcon: "/brand/fork/apple-touch-icon.png",
    socialPreview: "/brand/fork/social-preview.png",
    themeColor: "#6C6FF5",
    imageAlt: "Fork brand mark",
  },
  hire: {
    title: "ForkHire - AI Recruiting Platform",
    description: "ForkHire helps employers manage jobs, candidates, interviews, billing, and hiring workflows.",
    favicon: "/brand/forkhire/mark.svg",
    appleTouchIcon: "/brand/forkhire/apple-touch-icon.png",
    socialPreview: "/brand/forkhire/social-preview.png",
    themeColor: "#2B2E8F",
    imageAlt: "ForkHire brand mark",
  },
  seek: {
    title: "ForkSeek - AI Career Platform",
    description: "ForkSeek helps candidates build CVs, discover jobs, manage applications, and practice interviews.",
    favicon: "/brand/forkseek/mark.svg",
    appleTouchIcon: "/brand/forkseek/apple-touch-icon.png",
    socialPreview: "/brand/forkseek/social-preview.png",
    themeColor: "#2ECC71",
    imageAlt: "ForkSeek brand mark",
  },
  admin: {
    title: "ForkAdmin - Platform Control Center",
    description: "ForkAdmin manages Fork platform operations, billing, services, identity, and governance.",
    favicon: "/brand/fork/mark.svg",
    appleTouchIcon: "/brand/fork/apple-touch-icon.png",
    socialPreview: "/brand/fork/social-preview.png",
    themeColor: "#6C6FF5",
    imageAlt: "ForkAdmin brand mark",
  },
};

const forkHirePathPrefixes = [
  "/analytics",
  "/apply",
  "/candidates",
  "/change-password",
  "/checkout",
  "/contract-signing",
  "/dashboard",
  "/f2f",
  "/force-reset-password",
  "/interview",
  "/interviews",
  "/jobs",
  "/my-account",
  "/notifications",
  "/offer",
  "/organization",
  "/organization-setup",
  "/settings",
  "/talent",
  "/team",
  "/user-guide",
];

function isForkHirePath(path: string) {
  return forkHirePathPrefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

type PortalOriginConfig = Partial<Record<Exclude<Portal, "fork">, string>>;

const runtimePortalOrigins: PortalOriginConfig = {
  hire: import.meta.env.VITE_PUBLIC_HIRE_URL,
  seek: import.meta.env.VITE_PUBLIC_SEEK_URL,
  admin: import.meta.env.VITE_PUBLIC_ADMIN_URL,
};

function configuredPortalHostname(value: string | undefined) {
  try {
    return value ? new URL(value).hostname.toLowerCase() : "";
  } catch {
    return "";
  }
}

function isPortalHostname(host: string, portal: Exclude<Portal, "fork">, origins: PortalOriginConfig) {
  const configuredHost = configuredPortalHostname(origins[portal]);
  return host === configuredHost || host === `${portal}.fork.sa` || host.startsWith(`${portal}.`);
}

export function resolvePortal(
  pathname: string,
  search: string,
  hostname: string,
  portalOrigins: PortalOriginConfig = runtimePortalOrigins,
): Portal {
  const host = hostname.toLowerCase();
  const path = pathname.toLowerCase();
  const portalParam = new URLSearchParams(search).get("portal")?.toLowerCase();

  if (path === "/interview" || path.startsWith("/interview/")) {
    return "hire";
  }

  if (portalParam === "seek") {
    return "seek";
  }

  if (portalParam === "hire") {
    return "hire";
  }

  if (portalParam === "admin") {
    return "admin";
  }

  if (isPortalHostname(host, "seek", portalOrigins)) {
    return "seek";
  }

  if (isPortalHostname(host, "hire", portalOrigins)) {
    return "hire";
  }

  if (isPortalHostname(host, "admin", portalOrigins)) {
    return "admin";
  }

  if (path.startsWith("/seek")) {
    return "seek";
  }

  if (path.startsWith("/admin") || path.startsWith("/super-admin")) {
    return "admin";
  }

  if (path.startsWith("/hire") || isForkHirePath(path)) {
    return "hire";
  }

  return "fork";
}
